import { useCallback, useMemo } from "react";
import type {
  DateFilter,
  FilterValue,
  NumberFilter,
} from "@/components/filter-popover";
import type { SortDirection } from "@/types/table-column";
import {
  type BrowserStorageOptions,
  useTrackedSessionStorage,
} from "./use-tracked-browser-storage";

// Type for date range (used in campaign filters)
export interface DateRange {
  start?: string;
  end?: string;
}

// Base interface for filter configuration
export interface BaseListFilters {
  search?: string;
  sortColumn?: string;
  sortDirection?: SortDirection;
  // Index signature for all possible filter types
  [key: string]:
    | string
    | string[]
    | NumberFilter
    | DateFilter
    | DateRange
    | SortDirection
    | null
    | undefined;
}

interface ListFiltersOptions<T extends BaseListFilters, C, P> {
  storageKey: string;
  defaultSortColumn: string;
  defaultSortDirection: SortDirection;
  buildQueryParams: (
    filters: T,
    options: {
      searchTerm?: string;
      columns?: C[];
      limit?: number;
    },
  ) => P;
  migration?: Omit<
    BrowserStorageOptions<T>,
    "storage" | "organizationScoped"
  >["migration"];
}

/**
 * Generic hook for managing list filters with automatic organization scoping.
 *
 * Features:
 * - Persists filter selections in sessionStorage for tab-specific behavior
 * - Provides methods to update filters individually or all at once
 */
export function useListFilters<
  T extends BaseListFilters,
  C, // Column type
  P, // Query params type
>(options: ListFiltersOptions<T, C, P>) {
  const {
    storageKey,
    defaultSortColumn,
    defaultSortDirection,
    buildQueryParams,
    migration,
  } = options;

  // Get and set filters from sessionStorage for tab-specific behavior
  const [currentFilters, setCurrentFilters] = useTrackedSessionStorage<T>(
    storageKey,
    {} as T,
    migration ? { migration } : undefined,
  );

  // Update all filters at once
  const setFilters = useCallback(
    (filters: T) => {
      setCurrentFilters(filters);
    },
    [setCurrentFilters],
  );

  // Update a specific filter field - using string key for flexibility with column keys
  const updateFilter = useCallback(
    (key: string, value: FilterValue) => {
      setCurrentFilters((current) => {
        const updated = { ...(current || {}) };

        if (value === null || value === undefined) {
          // Remove the key entirely when value is null/undefined
          // This prevents storing { [key]: null } in the filters object
          delete updated[key];
          return updated as T;
        }

        // Allow setting any filter key - this enables adding new keys like sortColumn and sortDirection
        (updated as Record<string, FilterValue>)[key] = value;
        return updated as T;
      });
    },
    [setCurrentFilters],
  );

  // Clear a specific filter - using string key for flexibility with column keys
  const clearFilter = useCallback(
    (key: string) => {
      setCurrentFilters((current) => {
        const updated = { ...(current || {}) };
        delete updated[key];
        return updated as T;
      });
    },
    [setCurrentFilters],
  );

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setCurrentFilters({} as T);
  }, [setCurrentFilters]);

  // Get default sort settings
  const getDefaultSortSettings = useCallback(() => {
    return {
      sortColumn: defaultSortColumn,
      sortDirection: defaultSortDirection,
    };
  }, [defaultSortColumn, defaultSortDirection]);

  // Convert filters to API query parameters
  const getQueryParams = useCallback(
    (options?: { searchTerm?: string; columns?: C[]; limit?: number }) => {
      return buildQueryParams(currentFilters || ({} as T), options || {});
    },
    [currentFilters, buildQueryParams],
  );

  // Calculate if there are any filters (including search, but excluding sort)
  const hasFilters = useMemo(() => {
    if (!currentFilters) return false;

    // Check if there's a search filter
    const hasSearch =
      currentFilters.search !== undefined && currentFilters.search !== "";

    // Get all non-sort keys
    const filterKeys = Object.keys(currentFilters).filter(
      (key) => key !== "sortColumn" && key !== "sortDirection",
    );

    // Return true if there's either a search or other filters
    return filterKeys.length > 0 || hasSearch;
  }, [currentFilters]);

  return {
    filters: currentFilters || ({} as T),
    setFilters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    getDefaultSortSettings,
    getQueryParams,
    hasFilters,
  };
}
